import { forwardRef, useId, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
// 循環参照回避のため Input はバレル(./index)経由ではなく直接 import する
import { Input } from './Input'
import type { InputProps } from './Input'

// type は表示/非表示トグルで内部管理するため利用側からは受け取らない。
export type PasswordInputProps = Omit<InputProps, 'type'>

// アイコンボタン分の右余白。cn は append-only でクラス順の上書きを保証しないため、
// クラス(px-4)への競合追記ではなくインライン style で確定的に padding-right を確保する。
const TOGGLE_PADDING_RIGHT = '2.75rem'

/**
 * パスワード入力欄＋表示/非表示トグル（目のアイコン）。
 * ui/Input を内部再利用し、可視状態(visible)に応じて type を password/text で切替える。
 * 汎用部品として props(type除く)を透過するのみで、利用側固有ロジック(一致判定/送信等)は持たない。
 *
 * a11y の責務分離:
 *  - トグルボタンのアクセシブルネームは固定 aria-label（下記）で提供する。
 *  - 利用側が渡した aria-label は ...rest 経由で実 input（入力欄）に届く（ボタンには付かない）。
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ id, style, ...rest }, ref) {
    const [visible, setVisible] = useState(false)
    // id 未指定でも aria-controls とラベル関連付けが成立するよう安定 id をフォールバック生成する。
    const reactId = useId()
    const inputId = id ?? reactId

    return (
      <div className="relative">
        <Input
          ref={ref}
          id={inputId}
          style={{ ...style, paddingRight: TOGGLE_PADDING_RIGHT }}
          {...rest}
          // type は表示トグルが確定的に主導権を持つよう ...rest の後に置く（型を迂回した type 混入でも上書きされない）。
          type={visible ? 'text' : 'password'}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label="パスワードの表示を切り替え"
          aria-pressed={visible}
          aria-controls={inputId}
          className={
            'absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 ' +
            'hover:text-gray-700 focus:outline-none focus-visible:ring-2 ' +
            'focus-visible:ring-primary rounded-field'
          }
        >
          {visible ? (
            <EyeOff className="w-5 h-5" aria-hidden="true" />
          ) : (
            <Eye className="w-5 h-5" aria-hidden="true" />
          )}
        </button>
      </div>
    )
  },
)
